import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import moment from 'moment';
import { useAuth } from '../../Common/Configurations/AuthContext';
import Flatpickr from 'react-flatpickr';

const FlightReport = () => {
    const { user } = useAuth();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
   
    const [filters, setFilters] = useState({
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
    });

    const getFlightReport = async () => {
        if (!user?.cid) {
            setLoading(false);
            setError("User company information not found.");
            return;
        }

        setLoading(true);
        setError('');

        const formattedStartDate = moment(filters.startDate).format('YYYY-MM-DD');
        const formattedEndDate = moment(filters.endDate).format('YYYY-MM-DD');

        try {
            const url = `${config.FLIGHTREPORT}/${user.cid}?fromDate=${formattedStartDate}&toDate=${formattedEndDate}`;
            const response = await apiService.getAll(url); // Assumes apiService.getAll performs a GET request
            
            setReportData(response || []); 
            console.log("Flight report response:", response);

        } catch (err) {
            setError("Could not fetch the flight report. Please try again later.");
            console.error("Failed to fetch flight report:", err);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.cid) { 
            getFlightReport();
        }
    }, [user]); 

    const totalProfit = useMemo(() => {
        return reportData.reduce((total, booking) => total + (booking.markup || 0), 0);
    }, [reportData]);

    const handleDateChange = (name, selectedDates) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: selectedDates[0]
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        getFlightReport();
    };

    return (
        <div className="main-content">
            
                    
                    {/* --- CHANGE: Title is now INSIDE the filter card --- */}
                    <div className="card">
                        <div className="card-body">
                            <form onSubmit={handleSearch}>
                                <div className="row g-3 align-items-center">
                                    <div className="col-md-4">
                                        <label htmlFor="startDate" className="form-label">Start Date</label>
                                        <div className="input-group">
                                            <Flatpickr
                                                className="form-control"
                                                name="startDate"
                                                value={filters.startDate}
                                                onChange={dates => handleDateChange('startDate', dates)}
                                                options={{ dateFormat: "d-m-Y" }}
                                            />
                                            <span className="input-group-text">
                                                <i className="ri-calendar-line"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="endDate" className="form-label">End Date</label>
                                        <div className="input-group">
                                            <Flatpickr
                                                className="form-control"
                                                name="endDate"
                                                value={filters.endDate}
                                                onChange={dates => handleDateChange('endDate', dates)}
                                                options={{
                                                    dateFormat: "d-m-Y",
                                                    minDate: filters.startDate
                                                }}
                                            />
                                            <span className="input-group-text">
                                                <i className="ri-calendar-line"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-2 align-self-end">
                                        <button type="submit" className="btn btn-primary w-100">
                                            Search
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Report Table Section */}
                    <div className="card">
                        <div className="card-body">
                            {error && <div className="alert alert-danger">{error}</div>}
                            <div className="table-responsive">
                                <table className="table align-middle table-striped table-hover" id="reportTable">
                                    <thead className="bg-soft-info bg-gradient">
                                        <tr>
                                            <th>S NO</th>
                                            <th>Booking No</th>
                                            <th>Traveler Name</th>
                                            <th>Booking Status</th>
                                            <th>Payment Status</th>
                                            <th>Purchase Price</th>
                                            <th>Sale Price</th>
                                            <th>Profit</th>
                                        </tr>
                                    </thead>
                                    {loading ? (
                                        <tbody>
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    <div className="spinner-border text-primary my-3" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p>Loading Report, Please wait...</p>
                                                </td>
                                            </tr>
                                        </tbody>
                                    ) : (
                                        <>
                                            <tbody>
                                                {reportData.length > 0 ? reportData.map((booking, index) => (
                                                    <tr key={booking.flightBookingId}>
                                                        <td>{index + 1}</td>
                                                        <td><strong>{booking.bookingNo}</strong></td>
                                                        <td>{booking.travellerName}</td>
                                                        <td>
                                                            {booking.bookingStatus === "CONFIRMED" ?
                                                                <span className='badge bg-success'>{booking.bookingStatus}</span> :
                                                                <span className='badge bg-warning text-dark'>{booking.bookingStatus}</span>}
                                                        </td>
                                                        <td>
                                                            {booking.paymentStatus === "SUCCESS" ?
                                                                <span className='badge bg-success'>{booking.paymentStatus}</span> :
                                                                <span className='badge bg-danger'>{booking.paymentStatus}</span>}
                                                        </td>
                                                        <td>{(booking.netPrice || 0).toFixed(2)}</td>
                                                        <td>{(booking.totalPrice || 0).toFixed(2)}</td>
                                                        <td className='fw-bold text-success'>
                                                            {(booking.markup || 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4">No records found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            {reportData.length > 0 && (
                                                <tfoot>
                                                    <tr>
                                                        <td colSpan="7" className="text-end fw-bold fs-14">Grand Total Profit:</td>
                                                        <td className="fw-bold fs-14 text-success">{totalProfit.toFixed(2)}</td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
          
    );
};

export default FlightReport;